/* eslint max-classes-per-file: 0 */

import clsx from 'clsx';
import Storefront from '../utils/storefront';
import { formatMoney, removeEdgesAndNodes } from '../utils/storefront/format-resource';
import Slider from '../slider';
import { productGroupGet } from '../utils/storefront/queries';

// Helper function to encode the product ID
function encodeProductId(productId) {
  return btoa(`gid://shopify/Product/${productId}`);
}

// Fetch product group data using Storefront class
async function fetchProductGroupData(productId) {
  const storefront = Storefront.getInstance();
  const encodedProductId = encodeProductId(productId); // Encode the product ID
  const response = await storefront.request({
    query: productGroupGet,
    variables: { id: encodedProductId }
  });

  // Use removeEdgesAndNodes to format the data
  const formattedData = removeEdgesAndNodes(
    response.data?.product
  );
  return formattedData;
}

function getProductGroupProducts(productGroupData) {
  if (!productGroupData) return [];

  const products = productGroupData.products?.references || [];
  const products2 = productGroupData.products2?.references || [];
  const seen = new Set();

  return [...products, ...products2].filter((groupProduct) => {
    if (!groupProduct?.id || seen.has(groupProduct.id)) return false;
    seen.add(groupProduct.id);
    return true;
  });
}

const CAROUSEL_INIT_ROOT_MARGIN = '300px 0px 400px 0px';
const CAROUSEL_INITIALIZED_ATTR = 'data-carousel-initialized';

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.slider = null;
    this.clearCarouselInitialized();
    this.slidersContainer = this.querySelectorAll('[data-product-card]');
    this.badges = this.querySelectorAll('[data-badge]');
    this.mobileQuickViewButton = this.querySelector('[data-mobile-quick-view]');
    this.mobileQuickViewVariantId = this.querySelector('[data-product-card]').getAttribute('data-variant-id');
    this.observeCarouselInit();
    this.enableQuickView();

    this.startInjectingData();
  }

  disconnectedCallback() {
    this._carouselInitObserver?.disconnect();
    this._carouselInitObserver = null;
    if (this.slider?.swiper) {
      this.slider.destroy();
    }
    this.slider = null;
    this.clearCarouselInitialized();
  }

  resetSwatches = () => {
    this.swatches = this.querySelectorAll('[data-product-card-swatches] [data-swatch]');
    this.swatches.forEach((swatch) => {
      swatch.classList.remove('border-black');
      swatch.setAttribute('aria-selected', 'false');
    });
  }

  hideQuickView = (qButton) => {
    let parentWithAttribute = qButton;
    while (parentWithAttribute && !parentWithAttribute.hasAttribute('data-template-name')) {
      parentWithAttribute = parentWithAttribute.parentElement;
    }
    if (parentWithAttribute && parentWithAttribute.getAttribute('data-template-name') === 'product') {
      qButton.classList.add('!hidden');
      const buttonElement = qButton.querySelector('button');
      buttonElement.classList.add('!hidden');
    }
  }



  getCarouselEl = () => this.querySelector('[data-product-card-carousel]');

  isCarouselInitialized = () => Boolean(this.slider?.swiper);

  clearCarouselInitialized = (carouselEl = this.getCarouselEl()) => {
    carouselEl?.removeAttribute(CAROUSEL_INITIALIZED_ATTR);
    carouselEl?.classList.remove('is-carousel-initialized');
  };

  markCarouselInitialized = (carouselEl) => {
    carouselEl.setAttribute(CAROUSEL_INITIALIZED_ATTR, 'true');
    carouselEl.classList.add('is-carousel-initialized');
  };

  initSlider = () => {
    const carouselEl = this.getCarouselEl();
    if (!carouselEl) return this.slider;

    // Keep one Swiper instance per card; update it when slides change.
    if (this.isCarouselInitialized()) return this.slider;

    const nextEl = this.querySelector('[data-product-card-next]');
    const prevEl = this.querySelector('[data-product-card-prev]');
    this.slider = new Slider({
      el: carouselEl,
      options: {
        slidesPerView: 1,
        pagination: {
          el: this.querySelector('[data-product-card-pagination]'),
          clickable: false
        },
        navigation: {
          nextEl,
          prevEl,
        },
        on: {
          slideChange: (swiper) => {
            this.updateQuickViewTabindex(swiper);
          },
          init: (swiper) => {
            this.updateQuickViewTabindex(swiper);
          }
        }
      }
    });
    nextEl?.removeAttribute('aria-controls');
    prevEl?.removeAttribute('aria-controls');
    this.markCarouselInitialized(carouselEl);
    return this.slider;
  };

  updateQuickViewTabindex = ({ slides, activeIndex }) => {
    slides.forEach((slide, index) => {
      const quickViewButton = slide.querySelector('[data-quick-view] button');
      const isActive = index === activeIndex;
      
      // Update slide attributes
      if (isActive) {
        slide.setAttribute('aria-hidden', 'false');
        slide.setAttribute('tabindex', '0');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
      }
      
      // Update quick view button attributes
      if (quickViewButton) {
        if (isActive) {
          quickViewButton.setAttribute('tabindex', '0');
          quickViewButton.setAttribute('aria-hidden', 'false');
        } else {
          quickViewButton.setAttribute('tabindex', '-1');
          quickViewButton.setAttribute('aria-hidden', 'true');
        }
      }
    });
  };

  enableQuickView = () => {
    this.slidersContainer = this.querySelector('[data-product-card]');
    this.mobileQuickViewButton = this.querySelector('[data-mobile-quick-view]');
    this.quickViewButtons = this.slidersContainer.querySelectorAll('[data-quick-view]');
    this.quickViewMoreButton = this.querySelector('[data-more-swatches] [data-product-url]');
    const quickViewVariantId = this.slidersContainer.getAttribute('data-variant-id');
    const productHandle = this.slidersContainer.getAttribute('data-product-handle');
    const productId = this.slidersContainer.getAttribute('data-product-id');
    this.hideQuickView(this.quickViewButton);
    this.quickViewMoreButton.addEventListener('click', (e) => {
      e.preventDefault();
      const quickViewOpened = new CustomEvent('quick-view-opened', {
        detail: {
          productHandle: this.slidersContainer.getAttribute('data-product-handle'),
          productId: this.slidersContainer.getAttribute('data-product-id'),
          currentVariant: this.slidersContainer.getAttribute('data-variant-id'),
          clickedButton: e.target,
        }
      });
      document.dispatchEvent(quickViewOpened);
    }); 
    this.quickViewButtons.forEach((qViewButton) => {
      qViewButton.addEventListener('click', (e) => {
        e.preventDefault();
        const quickViewOpened = new CustomEvent('quick-view-opened', {
          detail: {
            productHandle,
            productId,
            currentVariant: quickViewVariantId,
            clickedButton: e.target,
          }
        });
        document.dispatchEvent(quickViewOpened);
      });
    });

    this.mobileQuickViewButton.replaceWith(
      this.mobileQuickViewButton.cloneNode(true)
    );
    this.mobileQuickViewButton = this.querySelector('[data-mobile-quick-view]');

    this.mobileQuickViewButton?.addEventListener('click', (e) => {
      e.preventDefault();
      const mobileQuickViewOpened = new CustomEvent('quick-view-opened', {
        detail: {
          productHandle,
          productId,
          currentVariant: quickViewVariantId,
          clickedButton: e.target,
        }
      });
      document.dispatchEvent(mobileQuickViewOpened);
    });
    this.hideQuickView(this.mobileQuickViewButton);
  }

  enableSwatches = () => {
    this.swatches = this.querySelectorAll('[data-product-card-swatches] [data-swatch]');
    this.swatches?.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const swatchVariantId = swatch.getAttribute('data-variant-id').split('/').pop();
        this.resetSwatches();
        swatch.classList.add('border', 'border-black');
        swatch.setAttribute('aria-selected', 'true');
        const variantData = this.findVariantData(swatchVariantId);
        this.updateProductCard(
          variantData.variant,
          variantData.productId,
          variantData.productHandle,
          variantData.productTitle,
          variantData.productCategory,
          variantData.productBadge
        );
      });
    });
  }

  findVariantData = (variantId) => {
    const { productGroupData, product: prod } = this;
    const groupProducts = getProductGroupProducts(productGroupData);
    for (const product of [prod].concat(groupProducts)) {
      for (const variant of product.variants) {
        if (variant.id.split('/').pop() === variantId) {
          return {
            variant,
            productId: product.id,
            productHandle: product.handle,
            productTitle: product.title,
            productCategory: variant.swatch.reference.label.value,
            productBadge: variant.badge?.value
          };
        }
      }
    }
    return null;
  }

  observeCarouselInit = () => {
    const carouselEl = this.getCarouselEl();
    if (!carouselEl) return;
    if (this.isCarouselInitialized()) return;

    this._carouselInitObserver?.disconnect();
    this._carouselInitObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (this.isCarouselInitialized()) {
            this._carouselInitObserver?.disconnect();
            this._carouselInitObserver = null;
            return;
          }
          this.initSlider();
          this._carouselInitObserver?.disconnect();
          this._carouselInitObserver = null;
        });
      },
      {
        root: null,
        rootMargin: CAROUSEL_INIT_ROOT_MARGIN,
        threshold: 0,
      }
    );
    this._carouselInitObserver.observe(carouselEl);
  };

  /** Swiper init after deferred load, or immediately when user updates the card (swatch). */
  ensureSliderInitialized = () => {
    const carouselEl = this.getCarouselEl();
    if (!carouselEl) return;
    if (this.isCarouselInitialized()) return;
    this._carouselInitObserver?.disconnect();
    this._carouselInitObserver = null;
    this.initSlider();
  };

  async fetchAndInjectProductGroupData() {
    const divWithProductId = this.querySelector('div');
    const productId = divWithProductId.getAttribute('data-product-id');
    const currentVariantId = divWithProductId.getAttribute('data-variant-id'); // Get current variant ID
    const product = await fetchProductGroupData(productId);
    const productGroupData = product.productGroup.reference;
    this.productGroupData = productGroupData; // Store productGroupData in the instance
    this.product = product;
    if (productGroupData) {
      this.injectSwatches(productGroupData, currentVariantId);
    }
  }

  injectSwatches(productGroupData, currentVariantId) {
    const swatchesContainer = this.querySelector('[data-product-card-swatches]');
    const swatches = getProductGroupProducts(productGroupData)
      .filter((product) =>
        !product.variants.some(variant => variant.id.split('/').pop() === currentVariantId)
      );
    swatches.slice(0, 4).forEach((product, i) => {
      product.variants.forEach(variant => {
        const swatchHtml = this.createSwatchHtml(
          variant, i
        );
        swatchesContainer.children[swatchesContainer.children.length - 1]
          .insertAdjacentHTML(
            'beforebegin',
            swatchHtml
          )
      });
    });

    this.toggleMoreSwatches(swatches.length + 1)
    this.enableSwatches();
  }

  toggleMoreSwatches(numTotalSwatches) {
    const classes = clsx(
      {
        'block md:hidden': numTotalSwatches > 3 && numTotalSwatches <= 5,
        'hidden': numTotalSwatches <= 3,
        'block': numTotalSwatches > 5,
      }
    );
    const el = this.querySelector('[data-more-swatches]');
    el.classList.remove('hidden');
    if (classes) {
      el.classList.add(...classes.split(' '));
    }
  }

  createSwatchHtml(variant, index) {
    const swatchImage = variant?.swatch?.reference?.
      image?.reference?.image?.src;
    const swatchLabel = variant?.swatch?.reference?.label?.value;
    const baseClasses = "h-[24px] w-full max-w-[48px] cursor-pointer leading-none border border-transparent flex items-center justify-center text-12 bg-grey-7";
    const liClasses = clsx(
      "w-full max-w-[48px]",
      index >= 2 && "hidden md:block"
    );
    /* eslint-disable */
    return `
      <li class="${liClasses}" role="none">
        <button class="${baseClasses}" role="option" aria-selected="false" data-swatch data-variant-id="${variant.id}">
          <img src="${swatchImage}" alt="${swatchLabel}" class="image w-full h-full swatch object-cover">
        </button>
      </li>
    `;
    /* eslint-enable */
  }

  updateProductCard = (
    variant,
    productId,
    productHandle,
    productTitle,
    productCategory,
    productBadge
  ) => {
    const dynamicProductCard = this.closest('dynamic-product-card');
    const productCardDiv = this.querySelector(`[data-product-card]`);
    const carouselEnabled = productCardDiv.getAttribute('data-carousel-enabled');
    const variantUrl = `/products/${productHandle}?variant=${variant.id.split('/').pop()}`;

    if (productCardDiv) {
      const imagesHtml = this.generateImagesHtml(
        variant, carouselEnabled, variantUrl
      );
      const compareAtPriceHtml = variant.compareAtPrice ? `
        <span class="line-through text-gray-500">
        ${formatMoney({
        amount: variant.compareAtPrice.amount,
        currencyCode: window.TMW?.localization?.currencyCode || 'USD',
      })}
        </span>
      ` : '';

      // Update the swiper-wrapper content
      const swiperWrapper = productCardDiv.querySelector('.swiper-wrapper');
      if (swiperWrapper) {
        const tpl = document.createElement('template');
        tpl.innerHTML = imagesHtml;
        swiperWrapper.replaceChildren(...tpl.content.childNodes);
      }

      const numericProductId = productId.split('/').pop();
      const numericVariantId = variant.id.split('/').pop();
      // Update data-variant-id
      productCardDiv.setAttribute('data-variant-id', numericVariantId);
      productCardDiv.setAttribute('data-product-id', numericProductId);
      productCardDiv.setAttribute('data-product-handle', productHandle);
      dynamicProductCard?.setAttribute('handle', productHandle);
      // Update data-product-card-title
      const productCardTitle = productCardDiv.querySelector('[data-product-card-title]');
      if (productCardTitle) {
        productCardTitle.href = variant.url;
        productCardTitle.innerHTML = productTitle;
      }

      // Update data-product-card-description
      const productCardDescription = productCardDiv.querySelector('[data-product-card-description]');
      if (productCardDescription) {
        productCardDescription.innerHTML = productCategory;
      }

      // Update product badge
      const productCardBadge = productCardDiv.querySelector('[data-badge]');
      if (productCardBadge) {
        productCardBadge.innerHTML = ''
      }

      if (productCardBadge && productBadge) {
        productCardBadge.innerHTML = productBadge
        productCardBadge.classList.remove('hidden');
      } else {
        productCardBadge?.classList.add('hidden');
      }

      // Update data-price
      const productCardPrice = productCardDiv.querySelector('[data-price]');
      if (productCardPrice) {
        productCardPrice.innerHTML = `
          ${compareAtPriceHtml}
          <span>
            ${formatMoney({
          amount: variant.price.amount,
          currencyCode: window.TMW?.localization?.currencyCode || 'USD',
        })}
          </span>
        `;
      }

      if (carouselEnabled) {
        // Slides are already replaced; update existing swiper instead of re-initializing.
        if (this.slider?.swiper) {
          this.slider.swiper.update();
          this.slider.swiper.slideTo(0, 0);
          this.updateQuickViewTabindex(this.slider.swiper);
        } else {
          this.ensureSliderInitialized();
        }
      }
      this.enableQuickView();
    }
  }

generateImagesHtml = (variant, carouselEnabled, variantUrl) => {
  let imagesHtml;
  if (carouselEnabled) {
    /* eslint-disable */
    imagesHtml = variant.images.references
      .filter(image => image.image)
      .map((image, index) => {
        const imageLoadingAttrs = index === 0
          ? (this.hasAttribute('data-priority-image')
            ? "loading='eager' fetchpriority='high'"
            : "loading='eager'")
          : "loading='lazy'";
        return `
        <div class='swiper-slide' role='group' aria-roledescription='slide' aria-label='Slide ${index + 1} of ${variant.images.references.filter(img => img.image).length}'>
          <span 
            class='relative block w-full cursor-pointer'
            tabindex='-1'
            aria-hidden='true'
            aria-label='${variant.title}'
            onclick='window.location.href = "${variantUrl}"'>
            <img 
            src='${image.image.desktopSrc}&width=354' 
            alt='' 
            class='w-full h-full object-cover' 
            width='354'
            height='354'
            ${imageLoadingAttrs}/>
          </span>
          <div class='hidden  transition-opacity duration-300 lg:block lg:absolute lg:w-full lg:bottom-2.5 lg:left-0 lg:px-1.5 group-hover:opacity-100' data-quick-view>
            <button class=' md:h-8 h-7
                          utility-4 text-primary bg-white bg-opacity-80 px-1.5 w-full
                          hover:border hover:border-black md:!leading-3 !leading-[11px]
                          group-hover:opacity-100
                          opacity-0 transition-opacity duration-300
                          focus:opacity-100'>
              Quick View
            </button>
          </div>
        </div>
      `;
      }).join('');
    /* eslint-enable */
  } else {
    /* eslint-disable */
    imagesHtml = `
      <div class='swiper-slide' role='group' aria-roledescription='slide' aria-label='Slide 1 of 1'>
        <span 
          class='relative block w-full cursor-pointer'
          tabindex='-1'
          aria-hidden='true'
          aria-label='${variant.title}'
          onclick='window.location.href = "${variantUrl}"'>
          <img 
          src='${variant.images.references[0]?.image.desktopSrc}&width=354' 
          alt='' 
          class='w-full h-full object-cover' 
          width='354'
          height='354'
          loading='eager'
          ${this.hasAttribute('data-priority-image') ? "fetchpriority='high'" : ''}
          />
        </span>
        <div class='hidden  transition-opacity duration-300 lg:block lg:absolute lg:w-full lg:bottom-2.5 lg:left-0 lg:px-1.5 group-hover:opacity-100' data-quick-view>
          <button class=' md:h-8 h-7
                          utility-4 text-primary bg-white bg-opacity-80 px-1.5 w-full
                          hover:border hover:border-black md:!leading-3 !leading-[11px]
                          group-hover:opacity-100
                          opacity-0 transition-opacity duration-300
                          focus:opacity-100'>
            Quick View
          </button>
        </div>
      </div>
    `;
    /* eslint-enable */
  }
  return imagesHtml;
}

  startInjectingData = () => {
    const observerOptions = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.fetchAndInjectProductGroupData();
          obs.unobserve(entry.target); // Stop observing after loading
        }
      });
    }, observerOptions);

    observer.observe(this);
  }
}

class DynamicProductCard extends HTMLElement {
  constructor() {
    super();

    this.variantId = this.getAttribute('variant-id');
    this.handle = this.getAttribute('handle');
    this.template = this.getAttribute('template') || 'product-card';
    this.urlPostfix = this.getAttribute('url-postfix') || '';
    this.displaySwatches = this.getAttribute('display-swatches') === 'true';
    this.CACHE = {};
    this.VARIANT_HANDLE_CACHE = {};
  }

  connectedCallback() {
    this.loadProduct();
  }

  async loadProduct() {
    const { handle } = this;

    if (!handle) return;

    const cacheKey = `${handle}-${this.variantId}-${this.template}`;
    if (this.CACHE[cacheKey]) {
      this.renderProduct(this.CACHE[cacheKey]);
      return;
    }

    try {
      const response = await fetch(`/products/${handle}?view=${this.template}&layout=none${this.variantId ? `&variant=${this.variantId}` : ''}`);
      let text = await response.text();

      if (text.includes('<html') || text.includes('<body')) {
        throw new Error('Invalid response');
      }
   
      if (this.displaySwatches) {
        text = text.replace(
          // eslint-disable-next-line max-len
          /(<div[^>]*class=['"][^'"]*\bswatches\b[^'"]*)\bflex\b([^'"]*['"][^>]*data-product-card-swatches[^>]*>)/gi,
          '$1hidden$2'
        );
      }
      this.CACHE[cacheKey] = text;
      this.renderProduct(text);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading product:', error);
    }
  }

  renderProduct(content) {
    this.innerHTML = content;

    this.updatePostfix();
  }

  updatePostfix() {
    if (!this.urlPostfix || !window.__VENDORS__?.nosto?.enabled) return;
    const links = this.querySelectorAll('[data-product-url]');
    links.forEach((link) => {
      const href = new URL(link.href || link.getAttribute('product-url'));
      const [key, value] = this.urlPostfix.split('=');
      if (key && value) {
        href.searchParams.append(key, value);
        // eslint-disable-next-line no-unused-expressions, no-param-reassign
        link.href ? (link.href = href.toString()) :
        link.setAttribute('product-url', href.toString());
      }
    });
  }
}

export default () => {
  if (!customElements.get('product-card'))
    customElements.define('product-card', ProductCard);

  if (!customElements.get('dynamic-product-card'))
    customElements.define('dynamic-product-card', DynamicProductCard);
}
